/** @format */
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeywordSearchInput, Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import LanguageSelectDropdown from 'src/components/LanguageSelectDropdown';
import { useDispatch, useSelector } from 'src/store';
import {
  deleteContent,
  getContents,
  getEditors,
  toggleContentPublished,
  resetFilters,
  setDateRange,
  setKeyword,
  setOnlyOwn,
  setSelectedEditors,
  setSelectedLanguage,
  setSelectedStatus
} from 'src/slices/contents';
import ExtranetContentAdminItem from 'src/views/administration/ExtranetContentAdminItem';
import ExtranetTimeframeCalendarDialog from 'src/components/ExtranetTimeframeCalendarDialog/ExtranetTimeframeCalendarDialog';
import Page from 'src/components/Page';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import { ArrowDownIcon, CalendarIcon } from 'src/components/icons';
import {
  formatExtranetLanguageList,
  getExtranetFilterLanguageItems,
  resolveExtranetApiLanguageCode
} from 'src/utils/extranetLanguages';
import { getDefaultSearchLanguage } from 'src/slices/search';
import AdministrationActionConfirmDialog from './AdministrationActionConfirmDialog';

const ADMIN_TABS = [
  { id: 'all', labelKey: 'administrationPage.extranetContents.tabs.all' },
  { id: 'mine', labelKey: 'administrationPage.extranetContents.tabs.mine' }
];

const CONTENT_STATUS_OPTIONS = [
  { key: 'all', labelKey: 'administrationPage.extranetContents.status.all' },
  { key: 'published', labelKey: 'administrationPage.extranetContents.status.published' },
  { key: 'removed', labelKey: 'administrationPage.extranetContents.status.removed' }
];

function formatDateValue(value) {
  if (!value) return '-';
  const casted = moment(value);
  return casted.isValid() ? casted.format('DD.MM.YYYY') : '-';
}

function mapContentRows(items, t) {
  return (Array.isArray(items) ? items : []).map((item, index) => {
    const rowId = item?.id ?? item?.uuid ?? item?.contentId ?? `content-row-${index}`;
    const langs = Array.isArray(item?.languages) ? item.languages : [];
    return {
      id: rowId,
      title: item?.title || item?.name || item?.headline || '-',
      released: formatDateValue(item?.releaseDate || item?.publishedAt || item?.releasedAt),
      created: formatDateValue(item?.creationDate || item?.createdAt || item?.date),
      removed: formatDateValue(item?.removedAt || item?.deletedAt),
      languages: langs.length > 0 ? formatExtranetLanguageList(langs, t) : '-',
      clicks: item?.clicks ?? item?.views ?? 0,
      published: item?.published !== false,
      locale:
        typeof item?.market === 'string' && item.market.trim()
          ? item.market.trim()
          : typeof item?.locale === 'string'
            ? item.locale.trim()
            : '',
      createdBy: '',
      description: item?.description || item?.teaser || item?.text || ''
    };
  });
}

function formatLabel(selectedKey, items, fallback) {
  return items.find(x => x.key === selectedKey)?.label || fallback;
}

function getEditorMenuGroups(items) {
  const uniqueEditors = Array.from(
    new Map(
      (Array.isArray(items) ? items : [])
        .filter(item => item?.label)
        .map(item => [String(item.key), { key: String(item.key), value: item.value, label: item.label }])
    ).values()
  ).sort((a, b) => a.label.localeCompare(b.label));
  const grouped = uniqueEditors.reduce((acc, editor) => {
    const group = editor.label.charAt(0).toUpperCase();
    if (!acc[group]) acc[group] = [];
    acc[group].push(editor);
    return acc;
  }, {});
  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b))
    .map(group => ({
      key: group,
      label: group,
      children: grouped[group]
    }));
}

export default function ExtranetContentsAdminPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    keyword,
    selectedLanguage,
    selectedStatus,
    selectedEditors,
    onlyOwn,
    editors,
    dateFrom,
    dateTo,
    results,
    resultsNextCursor,
    resultsLoading,
    resultsAppending,
    deleteLoading,
    deleteError,
    deletingContentId
  } = useSelector(state => state.contents);

  const languageItems = useMemo(() => getExtranetFilterLanguageItems(t), [t]);

  const [activeTab, setActiveTab] = useState('all');
  const [timeframePickerOpen, setTimeframePickerOpen] = useState(false);
  const timeframeRootRef = useRef(null);

  const [actionsDropdownOpenKey, setActionsDropdownOpenKey] = useState(null);

  const [deleteModalContent, setDeleteModalContent] = useState(null);
  const [deleteDialogError, setDeleteDialogError] = useState('');
  const [deleteContentLoading, setDeleteContentLoading] = useState(false);
  const [hideModalContent, setHideModalContent] = useState(null);
  const [hideDialogError, setHideDialogError] = useState('');
  const [rowActionError, setRowActionError] = useState('');
  const [togglingContentId, setTogglingContentId] = useState(null);

  useEffect(() => {
    if (!timeframePickerOpen) return undefined;
    const handlePointerDown = event => {
      if (timeframeRootRef.current && !timeframeRootRef.current.contains(event.target)) {
        setTimeframePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [timeframePickerOpen]);

  useEffect(() => {
    dispatch(setOnlyOwn(activeTab === 'mine'));
  }, [activeTab, dispatch]);

  useEffect(() => {
    dispatch(getEditors());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getContents());
  }, [dispatch, keyword, selectedLanguage, selectedStatus, selectedEditors, onlyOwn, dateFrom, dateTo]);

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  const rows = useMemo(() => mapContentRows(results, t), [results, t]);
  const hasRows = rows.length > 0;
  const shouldShowSkeleton = resultsLoading && !hasRows;
  const shouldDimList = resultsLoading && hasRows;
  const editorMenuGroups = useMemo(() => getEditorMenuGroups(editors), [editors]);
  const selectedEditor = selectedEditors[0] ? String(selectedEditors[0]) : 'all';
  const editorItems = useMemo(
    () =>
      [
        { key: 'all', label: t('administrationPage.extranetContents.editors.all') }
      ].concat(editors.map(editor => ({ key: String(editor.key), label: editor.label }))),
    [editors, t]
  );
  const contentStatusOptions = useMemo(
    () =>
      CONTENT_STATUS_OPTIONS.map(option => ({
        ...option,
        label: t(option.labelKey)
      })),
    [t]
  );

  const selectedLanguageLabel = useMemo(
    () => formatLabel(selectedLanguage, languageItems, 'Select Language'),
    [selectedLanguage, languageItems]
  );
  const selectedStatusKey = selectedStatus === true ? 'published' : selectedStatus === false ? 'removed' : 'all';
  const selectedStatusLabel = useMemo(
    () => formatLabel(selectedStatusKey, contentStatusOptions, 'Content Status'),
    [selectedStatusKey, contentStatusOptions]
  );
  const selectedEditorLabel = useMemo(
    () =>
      formatLabel(
        selectedEditor,
        editorItems,
        t('administrationPage.extranetContents.editors.label')
      ),
    [selectedEditor, editorItems, t]
  );
  const selectedTimeframeLabel = useMemo(() => {
    const start = dateFrom ? moment(dateFrom) : null;
    const end = dateTo ? moment(dateTo) : null;
    if (start?.isValid?.() && end?.isValid?.()) {
      return `${start.format('DD.MM.YYYY')} – ${end.format('DD.MM.YYYY')}`;
    }
    return t('administrationPage.extranetContents.timeframe');
  }, [dateFrom, dateTo]);

  const timeframeInitialRange = useMemo(() => {
    const start = dateFrom ? moment(dateFrom) : null;
    const end = dateTo ? moment(dateTo) : null;
    if (start?.isValid?.() && end?.isValid?.()) {
      return { startDate: start.toDate(), endDate: end.toDate() };
    }
    return { startDate: null, endDate: null };
  }, [dateFrom, dateTo]);

  const handleOpenEdit = itemId => {
    if (itemId === undefined || itemId === null) return;
    navigate(`/extranet/content/${encodeURIComponent(String(itemId))}/edit/${selectedLanguage}`);
  };
  const handleOpenView = itemId => {
    navigate(`/extranet/content/${itemId}/${selectedLanguage}`);
    // TODO: need to adjust the drawer page first, then re-enable this again
    // if (itemId === undefined || itemId === null) return;
    // const currentLocationState =
    //   location?.state && typeof location.state === 'object' ? { ...location.state } : undefined;
    // if (currentLocationState) {
    //   delete currentLocationState.backgroundLocation;
    //   delete currentLocationState.openAsDrawer;
    // }
    // navigate({
    //   pathname: `/extranet/content/${encodeURIComponent(String(itemId))}/${selectedLanguage}`,
    //   state: {
    //     backgroundLocation: {
    //       pathname: location.pathname,
    //       search: location.search,
    //       hash: location.hash,
    //       state: currentLocationState
    //     },
    //     openAsDrawer: true
    //   }
    // });
  };
  const handleRemove = contentId => {
    const content = rows.find(row => row.id === contentId);
    setDeleteModalContent(content);
  };

  const readActionError = (error, fallback) => {
    if (typeof error === 'string' && error.trim()) return error;
    if (typeof error?.message === 'string' && error.message.trim()) return error.message;
    return fallback;
  };

  const handleDuplicate = contentId => {
    if (contentId === undefined || contentId === null) return;
    setActionsDropdownOpenKey(null);
    setRowActionError('');
    navigate('/administration/content/create', { state: { duplicateFromId: contentId } });
  };

  const applyVisibilityToggle = async content => {
    if (!content || togglingContentId != null) return;
    setTogglingContentId(content.id);
    const locale = content.locale || resolveExtranetApiLanguageCode(selectedLanguage) || selectedLanguage;
    try {
      await dispatch(
        toggleContentPublished({
          contentId: content.id,
          published: content.published === false,
          locale
        })
      );
      setHideModalContent(null);
      setHideDialogError('');
    } catch (error) {
      const message = readActionError(error, t('administrationPage.extranetContents.toggleVisibilityError'));
      if (content.published !== false) {
        setHideDialogError(message);
      } else {
        setRowActionError(message);
      }
    } finally {
      setTogglingContentId(null);
    }
  };

  const handleToggleVisibility = contentId => {
    if (contentId === undefined || contentId === null || togglingContentId != null) return;
    const content = rows.find(row => row.id === contentId);
    if (!content) return;
    setActionsDropdownOpenKey(null);
    setRowActionError('');
    if (content.published === false) {
      applyVisibilityToggle(content);
      return;
    }
    setHideDialogError('');
    setHideModalContent(content);
  };

  const handleHideModalClose = () => {
    if (togglingContentId != null) return;
    setHideModalContent(null);
    setHideDialogError('');
  };

  const handleHideConfirm = () => {
    if (!hideModalContent) return;
    applyVisibilityToggle(hideModalContent);
  };

  const handleDeleteModalClose = () => {
    if (deleteContentLoading) return;
    setDeleteModalContent(null);
    setDeleteDialogError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalContent) return;

    try {
      setDeleteContentLoading(true);
      setDeleteDialogError('');
      await dispatch(deleteContent(deleteModalContent.id));
      setDeleteModalContent(null);
    } catch (error) {
      console.error('Delete content error:', error);
      setDeleteDialogError(error.message || t('administrationPage.deleteContentError'));
    } finally {
      setDeleteContentLoading(false);
    }
  };

  return (
    <Page
      className="administration-extranet-contents-page"
      title={t('common.contentDashboard')}>
      <Breadcrumb data={[{ title: t('common.contentDashboard') }]} />
      <div className="administration-extranet-contents-page__container container">
        <div className="administration-extranet-contents-page__top">
          <div className="administration-extranet-contents-page__heading-row">
            <h1 className="administration-extranet-contents-page__heading">
              {t('common.contentDashboard')}
            </h1>
            <div className="administration-extranet-contents-page__search">
              <KeywordSearchInput
                placeholder={t('administrationPage.extranetContents.searchPlaceholder')}
                onKeywordsChange={keywords => dispatch(setKeyword(keywords))}
              />
            </div>
          </div>

          <div
            className="administration-extranet-contents-page__tabs"
            role="tablist"
            aria-label={t('administrationPage.extranetContents.filtersTabsAria')}>
            {ADMIN_TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`administration-extranet-contents-page__tab${
                  tab.id === activeTab ? ' administration-extranet-contents-page__tab--active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={tab.id === activeTab}>
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="administration-extranet-contents-page__filters-row">
          <div className="administration-extranet-contents-page__filters-left">
            <LanguageSelectDropdown
              items={languageItems}
              selectedKey={selectedLanguage}
              label={selectedLanguageLabel}
              defaultValue={getDefaultSearchLanguage()}
              onReset={value => dispatch(setSelectedLanguage(value))}
              resetAriaLabel={t('common.button.resetToDefault')}
              onSelect={value => dispatch(setSelectedLanguage(value))}
            />

            <CustomDropDown
              placement="bottomLeft"
              value={selectedStatusKey}
              defaultValue="all"
              onReset={() => dispatch(setSelectedStatus(null))}
              resetAriaLabel={t('common.button.resetToDefault')}
              trigger={
                <button type="button" className="search-page__filter-trigger">
                  <span className="search-page__filter-trigger-label">{selectedStatusLabel}</span>
                  <ArrowDownIcon />
                </button>
              }>
              <ul className="custom-dropdown__menu" role="menu">
                {contentStatusOptions.map(item => (
                  <li className="custom-dropdown__menu-item" role="none" key={item.key}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        dispatch(
                          setSelectedStatus(item.key === 'published' ? true : item.key === 'removed' ? false : null)
                        )
                      }>
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </CustomDropDown>

            <CustomDropDown
              placement="bottomLeft"
              value={selectedEditor}
              defaultValue="all"
              onReset={() => dispatch(setSelectedEditors([]))}
              resetAriaLabel={t('common.button.resetToDefault')}
              trigger={
                <button type="button" className="search-page__filter-trigger">
                  <span className="search-page__filter-trigger-label">{selectedEditorLabel}</span>
                  <ArrowDownIcon />
                </button>
              }>
              <ul className="custom-dropdown__menu" role="menu">
                <li className="custom-dropdown__menu-item custom-dropdown__menu-item--group" role="none">
                  <button type="button" role="menuitem" onClick={() => dispatch(setSelectedEditors([]))}>
                    {t('administrationPage.extranetContents.editors.all')}
                  </button>
                </li>
                {editorMenuGroups.map(group => (
                  <li
                    className="custom-dropdown__menu-item custom-dropdown__menu-item--group"
                    role="none"
                    key={group.key}>
                    <span className="custom-dropdown__menu-group-label">{group.label}</span>
                    <ul className="custom-dropdown__menu custom-dropdown__menu--nested" role="none">
                      {(group.children || []).map(option => (
                        <li className="custom-dropdown__menu-item" role="none" key={option.key}>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => dispatch(setSelectedEditors([option.value]))}>
                            {option.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </CustomDropDown>
          </div>

          <div className="administration-extranet-contents-page__filters-right">
            <div
              ref={timeframeRootRef}
              className={`administration-extranet-contents-page__timeframe extranet-timeframe${
                timeframePickerOpen ? ' extranet-timeframe--open' : ''
              }`}>
              <button
                type="button"
                className="extranet-timeframe__trigger"
                onClick={() => setTimeframePickerOpen(open => !open)}
                aria-haspopup="dialog"
                aria-expanded={timeframePickerOpen}>
                <span className="extranet-timeframe__trigger-label">{selectedTimeframeLabel}</span>
                <CalendarIcon />
              </button>
              <ExtranetTimeframeCalendarDialog
                open={timeframePickerOpen}
                initialRange={timeframeInitialRange}
                onDismiss={() => setTimeframePickerOpen(false)}
                onClear={() => {
                  dispatch(setDateRange({ dateFrom: null, dateTo: null }));
                  setTimeframePickerOpen(false);
                }}
                onConfirm={({ startDate, endDate }) => {
                  dispatch(setDateRange({ dateFrom: startDate, dateTo: endDate }));
                  setTimeframePickerOpen(false);
                }}
              />
            </div>
          </div>
        </div>

          {rowActionError ? (
            <p className="administration-extranet-contents-page__action-error" role="alert">
              {rowActionError}
            </p>
          ) : null}

          <div
            className="administration-extranet-contents-page__table"
          role="table"
          aria-label={t('administrationPage.extranetContents.tableAria')}>
          <div className="administration-extranet-contents-page__header-row" role="rowgroup">
            <div className="administration-extranet-contents-page__header-cell" role="columnheader">
              {t('administrationPage.extranetContents.columns.title')}
            </div>
            <div className="administration-extranet-contents-page__header-cell" role="columnheader">
              {t('administrationPage.extranetContents.columns.released')}
            </div>
            <div className="administration-extranet-contents-page__header-cell" role="columnheader">
              {t('common.created')}
            </div>
            <div className="administration-extranet-contents-page__header-cell" role="columnheader">
              {t('administrationPage.extranetContents.columns.removed')}
            </div>
            <div className="administration-extranet-contents-page__header-cell" role="columnheader">
              {t('administrationPage.extranetContents.columns.languages')}
            </div>
            <div className="administration-extranet-contents-page__header-cell" role="columnheader">
              {t('administrationPage.extranetContents.columns.clicks')}
            </div>
            <div className="administration-extranet-contents-page__header-cell administration-extranet-contents-page__header-cell--actions">
              <span className="visually-hidden">
                {t('administrationPage.extranetContents.columns.actions')}
              </span>
            </div>
          </div>

          <div
            className={`administration-extranet-contents-page__body-group${
              shouldDimList ? ' administration-extranet-contents-page__body-group--dimmed' : ''
            }`}
            role="rowgroup">
            {shouldShowSkeleton
              ? Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={`content-skeleton-row-${index}`}
                    className="administration-extranet-contents-page__data-row administration-extranet-contents-page__data-row--skeleton"
                    role="row"
                    aria-hidden="true">
                    <div className="administration-extranet-contents-page__cell administration-extranet-contents-page__cell--title">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--title" />
                    </div>
                    <div className="administration-extranet-contents-page__cell cell--released">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--date" />
                    </div>
                    <div className="administration-extranet-contents-page__cell cell--created">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--date" />
                    </div>
                    <div className="administration-extranet-contents-page__cell cell--removed">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--date" />
                    </div>
                    <div className="administration-extranet-contents-page__cell cell--languages">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--languages" />
                    </div>
                    <div className="administration-extranet-contents-page__cell cell--clicks">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--clicks" />
                    </div>
                    <div className="administration-extranet-contents-page__cell administration-extranet-contents-page__cell--actions">
                      <div className="administration-extranet-contents-page__skeleton-line administration-extranet-contents-page__skeleton-line--actions" />
                    </div>
                  </div>
                ))
              : rows.map(row => (
                  <ExtranetContentAdminItem
                    key={row.id}
                    row={row}
                    isActionsOpen={actionsDropdownOpenKey === row.id}
                    onActionsOpenChange={open => setActionsDropdownOpenKey(open ? row.id : null)}
                    onEdit={handleOpenEdit}
                    onDuplicate={handleDuplicate}
                    onToggleVisibility={handleToggleVisibility}
                    onView={handleOpenView}
                    onRemove={handleRemove}
                    isVisibilityPending={togglingContentId === row.id}
                  />
                ))}
          </div>
        </div>

        {resultsNextCursor && (
          <div className="administration-extranet-contents-page__footer-actions">
            <Button
              type="button"
              text
              disabled={resultsLoading || resultsAppending}
              onClick={() => dispatch(getContents({ append: true, cursor: resultsNextCursor }))}
              additionalClassName="administration-extranet-contents-page__load-more">
              {t('common.button.loadMore')}
            </Button>
          </div>
        )}
      </div>

      <AdministrationActionConfirmDialog
        open={Boolean(hideModalContent)}
        onClose={handleHideModalClose}
        onConfirm={handleHideConfirm}
        title={t('administrationPage.extranetContents.confirmHideTitle')}
        message={`${t('administrationPage.extranetContents.confirmHideQuestion')} "${hideModalContent?.title}"?`}
        cancelLabel={t('administrationPage.confirmNo')}
        confirmLabel={t('administrationPage.confirmYes')}
        error={hideDialogError || undefined}
        confirmDisabled={togglingContentId != null}
      />

      <AdministrationActionConfirmDialog
        open={Boolean(deleteModalContent)}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        title={t('administrationPage.confirmDeleteTitle')}
        message={`${t('administrationPage.confirmDeleteQuestion')} "${deleteModalContent?.title}"?`}
        cancelLabel={t('administrationPage.confirmNo')}
        confirmLabel={t('administrationPage.confirmYes')}
        error={deleteDialogError || deleteError || undefined}
        confirmDisabled={deleteContentLoading || deleteLoading}
      />
    </Page>
  );
}
